import {RetouchMenu} from './menu/print-tone-collection.menu';
import {RetouchPreview} from './preview/retouch.preview';
import {ToneCorrectionFooter} from './footer/tone-collection-footer';
import {RetouchUseImage} from './use-image/print_tone_correction.use-image';
import './print-tone-correction.scss';
import { PrintToneCollectionHeader } from './header/print-tone-collection.header';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { orderLockActions } from '../../../slices/lock-order-slice';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { PathParams, RoutingPath } from '../../../routes/routing-path';
import { xmlActions } from '../../../xml/slice/xml-slice';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import * as lodash from 'lodash';
import { RetouchGetResponse } from '../../../api/back/retouch/api-retouch';
import { push } from 'connected-react-router';
import { localStorageActions } from '../../../slices/local-storage-slice';
import { usePreparation } from '../one-stop-order-preparation/hooks/use-preparation';
import { useLoading } from '../../ui/loading/hooks/use-loading';
import { usePrintOrder } from '../sift/hooks/use-print-order';
import { usePrintStatus } from '../sift/hooks/use-print-status';
import { usePrintLock } from '../sift/hooks/use-print-lock';
import { ToneCorrectionDataEntity } from '../../../models/sift/json/print-model';
import { PrintImagesDataEntity } from '../../../api/sift/front/api-print-image';
import { dialogActions } from '../../dialog/slice/dialog-slice';
import { cloneDeep } from 'lodash';
import { printActions } from '../../../slices/sift/print-slice';
import { Checkbox } from '../../ui/input/checkbox';
import { TextArea } from '../../ui/textarea/textarea';
import { commonActions } from '../../../slices/common-slice';
import { printOrderLockActions } from '../../../slices/lock-order-slice.print';
import { TrimEditableImage } from '../../../trim-editor/manager/image-edit/trim.editable-image';

export type ToneCorrectionImageData = {
  editableImage?: TrimEditableImage,
  toneCorrection?: ToneCorrectionDataEntity,
} & PrintImagesDataEntity;
export const RetouchContext = createContext<{ preparationPath: string }>({ preparationPath: '' })
export type InitRetouchCheck = {selectId: string, retouchId: string[]}[];
export const PrintToneCorrection = () => {
  const { kijshopCd, shopOrderId, orderId } = useParams<PathParams>();
  
  useEffect(() => {
    dispatch(printOrderLockActions.check(kijshopCd,shopOrderId,{
      yes:()=> {
        dispatch(orderLockActions.setId(shopOrderId));
      },
      no: () => {
        dispatch(orderLockActions.setId(''));
        dispatch(push(RoutingPath.cnv.print({ kijshopCd })));
      }
    }));
  }, []);

  useEffect(() => {
    dispatch(localStorageActions.getLocalData(kijshopCd));
  }, [kijshopCd]);

  /* ここから */
  usePrintStatus({ isLockedAfterOrder: true });
  const lockActions = usePrintLock();
  const dispatch = useAppDispatch();
  const {
    currentData,
    sortState,
  } = useAppSelector((state) => ({
    currentData: state.print.printShopOrderData[shopOrderId]?.meta,
    sortState: state.storage.data.toneCorrectionMenu.sortType,
  }));
  const {
    setLoadingMessage,
    LoadingPopup,
  }  = useLoading({});

  const {
    getData,
    getMetaData,
    getImages,
    editableImages,
  } = usePrintOrder();
  const [toneCorrectionList, setToneCorrectionList] = useState<ToneCorrectionDataEntity[]>([]);
  const [images, setImages] = useState<PrintImagesDataEntity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const viewImages = useMemo(() => {
    if (!currentData) return [];
    const origin = images.filter((v) => currentData?.getUseImageIdList().includes(v.id)).map((v) => ({
      ...v,
      editableImage: editableImages.find((v2) => v2.id === v.id),
      toneCorrection: toneCorrectionList.find((v2) => v2.id === v.id),
    }));
    const formatDate = (exifDate: string) => {
      return typeof exifDate !== 'string' ? new Date('') : new Date(exifDate);
    }
    const filter = origin.filter((v) => !!v.editableImage);
    switch(sortState) {
      case 'asc-name': {
        filter.sort((a, b) => a.orgFilename > b.orgFilename ? 1 : -1);
        break;
      }
      case 'dec-name': {
        filter.sort((a, b) => a.orgFilename < b.orgFilename ? 1 : -1);
        break;
      }
      case 'asc-date': {
        filter.sort((a, b) => formatDate(a.exifCreated) > formatDate(b.exifCreated) ? 1 : -1);
        break;
      }
      case 'dec-date': {
        filter.sort((a, b) => formatDate(a.exifCreated) < formatDate(b.exifCreated) ? 1 : -1);
        break;
      }
}
    return filter;
  }, [
    images,
    sortState,
    editableImages,
    currentData,
    toneCorrectionList,
  ]);

  const allCheckState = useMemo(() => viewImages.every((v) => v.toneCorrection?.value), [viewImages]);
  
  const allToggle = useCallback(() => {
    setToneCorrectionList((prev) => {
      return [...prev.map((v) => ({
        ...v,
        value: !allCheckState,
      }))]
    });
  }, [allCheckState]);
  const handlerCheckItem = useCallback((item: ToneCorrectionImageData) => {
    const clone = cloneDeep(toneCorrectionList);
    const target = clone.find((tone) => item.id === tone.id);
    if (!target) {
      clone.push({ id: item.id, memo: '', value: true })
    } else {
      target.value = !target.value;
    };
    setToneCorrectionList(clone);

  }, [
    toneCorrectionList,
  ]);
  const handlerClickSave = useCallback(async() => {
    if (!currentData) return;
    await lockActions.check();
    dispatch(dialogActions.pushMessage({
      title: '確認',
      message: [
        '色補正指示を反映させます。',
        'よろしいですか？',
      ],
      buttons: [
        { label: 'キャンセル', callback: () => dispatch(dialogActions.pop()) },
        { label: 'OK', callback: () => {
          dispatch(dialogActions.pop());
          setLoadingMessage('保存処理中です...');
          const structure = cloneDeep(currentData);
          structure.toneCorrection = [...toneCorrectionList];
          structure.getItemList().forEach((item) => {
            if (
              !item.images.filter((v) => v.count)
                .find((v) => {
                  return v.trimmingStatus === 3
                    || toneCorrectionList.find((v2) => v2.value && v2.id === v.id)
                })
            ) {
              item.memo = undefined;
            }
          })
          dispatch(printActions.shopOrder({ kijshopCd, shopOrderId }).update(structure, {
            success: () => {
              setLoadingMessage('');
              dispatch(push(RoutingPath.cnv.printOrder({ kijshopCd, shopOrderId })));
            },
            error: () => {
              setLoadingMessage('');
              dispatch(dialogActions.pushMessage({
                title: 'エラー',
                message: [
                  '色調補正指示の保存に失敗しました。',
                  '時間をおいて再度お試しください。',
                ],
                buttons: [
                  { label: 'OK', callback: () => dispatch(dialogActions.pop()) },
                ]
              }));
            }
          }));
        } },
      ]
    }))
  }, [
    currentData,
    dispatch,
    kijshopCd, shopOrderId,
    setLoadingMessage,
    toneCorrectionList,
    lockActions,
  ]);

  useEffect(() => {
    setLoadingMessage('画像情報を取得しています...');
    getData().then((res) => {
      setToneCorrectionList(res.meta?.toneCorrection ?? []);
      let total = 0;
      let loadingImageCount = 0;
      getImages({
        callback: {
          onCreateEditableImage: () => {
            loadingImageCount++;
            setLoadingMessage((`使用画像の読み込み中です。(${loadingImageCount}/${total})`));
            setIsLoading(true);
            if (total <= loadingImageCount) {
              setLoadingMessage('');

            }
          },
        },
      }).then((images) => {
        const _structure = res.meta;
        if (_structure) {
          const filterData = images.fileList.filter((v) => _structure.getUseImageIdList().includes(v.id));
          total = filterData.length;
          setImages(images.fileList);
        }
      });
    });
    getMetaData();
    dispatch(commonActions.getData(kijshopCd, { version: true, masterShop: true }));
  }, []);


  return (
    <div className="print_tone_correction">
      <div className="print_tone_correction__header_wrap">
        <div>
          {/* ヘッダー */}
          <PrintToneCollectionHeader />
        </div>
        <div>
          {/* メインメニュー */}
          <RetouchMenu />
        </div>
      </div>

      <div className="print_tone_correction__body">
        <div className="print_tone_correction__body__list">
          <RetouchUseImage 
            viewImages={viewImages}
            allCheck={allCheckState}
            handlerChangeAllCheck={allToggle}
            handlerCheckItem={handlerCheckItem}
            structure={currentData}
            isLoading={isLoading}
          />
        </div>

        {/* <RetouchPreview /> */}
      </div>

      <div className="print_tone_correction__footer">
        <ToneCorrectionFooter
          callbackSave={() => handlerClickSave()}
        />
      </div>
      <LoadingPopup />
    </div>
  );
};
